import some from "lodash/some";
import { InterestQuestionsSelector } from "PFApp/components/activity_form/form_selectors/interest_questions_selector";
import {
  FIELD_INTEREST_QUESTIONS_KEY,
  FIELD_NAME_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { TemplateKey } from "PFApp/constants/templates";
import { useInterestQuestionariesTemplateConfiguration, useTemplate } from "PFApp/hooks";
import { useWatchIsNew } from "PFApp/hooks/activity_form/multi_roles_form/use_watch_is_new";
import { useInitialQuestionsValues } from "PFApp/hooks/activity_form/use_initial_questions_values";
import { RoleFormKey } from "PFApp/types/activity_form/activity_form_schema";
import { Flex, Stack } from "PFComponents/containers";
import { InputFocusProps } from "PFComponents/table/interactive/use_cell_input_focus";
import Toggle from "PFComponents/toggle/toggle";
import { Typography } from "PFComponents/typography";
import { Activity } from "PFCore/types";
import { RefObject, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { SidePanelCellFormSection } from "../side_panel_cell_form_section";

type InterestQuestionsCellProps = {
  questions?: string[] | null;
  onChange: (questions?: string[] | null) => void;
  disabled?: boolean;
  roleFormKey: RoleFormKey;
  error?: string;
  inputFocusProps?: InputFocusProps;
  buttonRef?: RefObject<HTMLButtonElement>;
};

export const InterestQuestionsCell = ({
  questions,
  onChange,
  disabled,
  roleFormKey,
  error,
  inputFocusProps,
  buttonRef
}: InterestQuestionsCellProps) => {
  const { t: tActivities } = useTranslation("activities", {
    keyPrefix: "form.sections"
  });
  const { t } = useTranslation();
  const { t: tBlueprints } = useTranslation("activities", { keyPrefix: "engagementBlueprint" });

  const { setValue } = useFormContext();
  const roleName = useWatch({ name: `${roleFormKey}.${FIELD_NAME_KEY}` });
  const isNew = useWatchIsNew(roleFormKey);

  const roleTemplate = useTemplate(TemplateKey.Role)!;
  const { getInitialQuestionsValues } = useInitialQuestionsValues({
    template: roleTemplate
  });
  const templateConfig = useInterestQuestionariesTemplateConfiguration(roleTemplate);

  const isToggleVisible = templateConfig?.allowRmToEnable && isNew;
  const areQuestionsEditable = templateConfig?.allowRmToEdit && isNew;

  const handleChange = (questions?: string[]) => {
    onChange(questions);
  };

  const handleToggleChange = (isEnabled: boolean) => {
    // TODO: [SP-7181] Check if it is existing or new role and pass activity, isEdit accordingly
    const { initialQuestionsValues } = getInitialQuestionsValues({
      inheritFromActivity: !isNew,
      isClone: false,
      activity: {} as Activity
    });
    onChange(isEnabled ? initialQuestionsValues : null);
  };

  useEffect(() => {
    if (questions && !some(questions || [], Boolean)) {
      // Use setValue instead of onChange to avoid the field becoming dirty
      setValue(`${roleFormKey}.${FIELD_INTEREST_QUESTIONS_KEY}`, null);
    }
  }, [questions]);

  const anyQuestions = questions && questions.length > 0;
  const showSidePanel = isNew || (!isNew && anyQuestions);

  return (
    <SidePanelCellFormSection
      label={
        anyQuestions
          ? tActivities("showInterestQuestions.questionsEnabled")
          : tActivities("showInterestQuestions.questionsDisabled")
      }
      sidePanelTitle={tActivities("showInterestQuestions.title")}
      disabled={disabled}
      error={error}
      buttonText={isNew ? t("edit") : t("show")}
      editAriaLabel={tBlueprints("editInterestQuestionsInRole", {
        roleName: roleName || tBlueprints("emptyRoleName")
      })}
      inputFocusProps={inputFocusProps}
      buttonRef={buttonRef}
    >
      {showSidePanel && (
        <Stack gap="spacingMd">
          <Flex alignItems="center" justifyContent="space-between">
            <Typography variant="bodyRegular">{tActivities("showInterestQuestions.title")}</Typography>
            {isToggleVisible && (
              <Toggle onChange={handleToggleChange} checked={!!questions} disabled={disabled} />
            )}
          </Flex>

          {!!questions && (
            <InterestQuestionsSelector
              disabled={disabled || !areQuestionsEditable}
              questions={questions}
              onQuestionsChange={handleChange}
            />
          )}
        </Stack>
      )}
    </SidePanelCellFormSection>
  );
};
